/**
 * Calcule les mesures de couleur des regles de design.
 * Les conversions partagent la meme normalisation hexadecimale pour
 * comparer les creations sans dependance au rendu.
 */
export type TRgb = { r: number; g: number; b: number };
export type THsl = { h: number; s: number; l: number };

/** Normalise une couleur hexadecimale pour comparer les saisies. */
export const normalizeHex = (input: string | undefined | null): string | null => {
  if (typeof input !== 'string') return null;
  const raw = input.trim().replace(/^#/, '').toLowerCase();
  if (/^[0-9a-f]{3}$/.test(raw)) return `#${raw.split('').map(c => c + c).join('')}`;
  return /^[0-9a-f]{6}$/.test(raw) ? `#${raw}` : null;
};

/** Convertit un hex valide en canaux RGB. */
export const hexToRgb = (hex: string): TRgb | null => {
  const color = normalizeHex(hex);
  if (!color) return null;
  return {
    r: parseInt(color.slice(1, 3), 16),
    g: parseInt(color.slice(3, 5), 16),
    b: parseInt(color.slice(5, 7), 16),
  };
};

const linear = (channel: number): number => {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

/** Retourne la luminance WCAG utilisee par les regles de contraste. */
export const relativeLuminance = (hex: string): number | null => {
  const rgb = hexToRgb(hex);
  return rgb ? 0.2126 * linear(rgb.r) + 0.7152 * linear(rgb.g) + 0.0722 * linear(rgb.b) : null;
};

/** Retourne le rapport de contraste de deux couleurs valides. */
export const contrastRatio = (a: string, b: string): number | null => {
  const left = relativeLuminance(a);
  const right = relativeLuminance(b);
  if (left === null || right === null) return null;
  return (Math.max(left, right) + 0.05) / (Math.min(left, right) + 0.05);
};

/** Convertit un hex en teinte, saturation et luminosite. */
export const hexToHsl = (hex: string): THsl | null => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;
  let h = 0;
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h = (h * 60 + 360) % 360;
  }
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  return { h, s, l };
};

/** Conversion sRGB vers Lab D65, puis distance euclidienne CIE76. */
const toLab = (hex: string): [number, number, number] | null => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const r = linear(rgb.r);
  const g = linear(rgb.g);
  const b = linear(rgb.b);
  const x = (0.4124564 * r + 0.3575761 * g + 0.1804375 * b) / 0.95047;
  const y = 0.2126729 * r + 0.7151522 * g + 0.0721750 * b;
  const z = (0.0193339 * r + 0.1191920 * g + 0.9503041 * b) / 1.08883;
  const f = (v: number): number => v > 216 / 24389 ? Math.cbrt(v) : v * (24389 / 27) / 116 + 16 / 116;
  const fx = f(x);
  const fy = f(y);
  const fz = f(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
};

/** Mesure la distance CIE76 pour les seuils de difference perceptuelle. */
export const deltaE76 = (a: string, b: string): number | null => {
  const left = toLab(a);
  const right = toLab(b);
  return left && right
    ? Math.hypot(left[0] - right[0], left[1] - right[1], left[2] - right[2]) : null;
};

/** Garde un representant par groupe de couleurs assez proches. */
export const distinctColors = (hexes: string[], threshold = 3): string[] => {
  if (!Array.isArray(hexes)) return [];
  const kept: string[] = [];
  for (const raw of hexes) {
    const color = normalizeHex(raw);
    if (color && !kept.some(existing =>
      (deltaE76(existing, color) ?? Infinity) < threshold)) kept.push(color);
  }
  return kept;
};

/** Ecarte les neutres des calculs d'harmonie chromatique. */
export const isNeutral = (hex: string): boolean => {
  const hsl = hexToHsl(hex);
  return hsl !== null && (hsl.s < 0.12 || hsl.l < 0.08 || hsl.l > 0.95);
};
